import type { UseFetchOptions } from '#app';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

const DATABASE_NAME = 'sgm-trans-offline-requests';
const STORE_NAME = 'requests';
const GET_CACHE_STORE_NAME = 'get-cache';
const NATIVE_GET_CACHE_PREFIX = 'offline-get-';

type FormDataValue = {
	name: string;
	value: string | Blob;
	filename?: string;
};

type QueueableMethod = 'POST' | 'PUT' | 'PATCH' | 'DELETE';

type StoredBody =
	| { type: 'form-data'; entries: FormDataValue[] }
	| { type: 'value'; value: UseFetchOptions<unknown>['body'] };

export interface OfflineRequest {
	id: number;
	url: string;
	method: QueueableMethod;
	query?: UseFetchOptions<unknown>['query'];
	body?: StoredBody;
	headers: Record<string, string>;
	createdAt: number;
}

type NewOfflineRequest = Omit<OfflineRequest, 'id' | 'createdAt'>;

export interface CachedGetRequest {
	key: string;
	url: string;
	query?: UseFetchOptions<unknown>['query'];
	headers: Record<string, string>;
	tokenKey: string;
	response: unknown;
	updatedAt: number;
}

let databasePromise: Promise<IDBDatabase> | null = null;

const openDatabase = () => {
	if (databasePromise) return databasePromise;

	databasePromise = new Promise<IDBDatabase>((resolve, reject) => {
		const request = indexedDB.open(DATABASE_NAME, 2);
		request.onupgradeneeded = () => {
			if (!request.result.objectStoreNames.contains(STORE_NAME)) {
				request.result.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
			}
			if (!request.result.objectStoreNames.contains(GET_CACHE_STORE_NAME)) {
				request.result.createObjectStore(GET_CACHE_STORE_NAME, { keyPath: 'key' });
			}
		};
		request.onsuccess = () => resolve(request.result);
		request.onerror = () => reject(request.error || new Error('Failed to open offline request storage.'));
		request.onblocked = () => reject(new Error('Offline request storage is blocked by another page.'));
	}).catch((error) => {
		databasePromise = null;
		throw error;
	});

	return databasePromise;
};

const requestResult = <T>(request: IDBRequest<T>) =>
	new Promise<T>((resolve, reject) => {
		request.onsuccess = () => resolve(request.result);
		request.onerror = () => reject(request.error || new Error('Offline request storage operation failed.'));
	});

const transactionComplete = (transaction: IDBTransaction) =>
	new Promise<void>((resolve, reject) => {
		transaction.oncomplete = () => resolve();
		transaction.onerror = () => reject(transaction.error || new Error('Offline request storage transaction failed.'));
		transaction.onabort = () => reject(transaction.error || new Error('Offline request storage transaction was aborted.'));
	});

const encodeBody = (body: UseFetchOptions<unknown>['body']): StoredBody | undefined => {
	if (body === undefined) return undefined;
	if (body instanceof FormData) {
		const entries: FormDataValue[] = [];
		body.forEach((value, name) => {
			if (typeof value === 'string') {
				entries.push({ name, value });
			} else {
				entries.push({
					name,
					value,
					...(value instanceof File && { filename: value.name })
				});
			}
		});
		return { type: 'form-data', entries };
	}

	return { type: 'value', value: body };
};

export const decodeQueuedBody = (body?: StoredBody): UseFetchOptions<unknown>['body'] | undefined => {
	if (!body) return undefined;
	if (body.type === 'value') return body.value;

	const formData = new FormData();
	for (const entry of body.entries) {
		if (typeof entry.value === 'string') {
			formData.append(entry.name, entry.value);
		} else {
			formData.append(entry.name, entry.value, entry.filename);
		}
	}
	return formData;
};

const safeHeaders = (headers?: HeadersInit) => {
	const safe: Record<string, string> = {};
	if (!headers) return safe;

	new Headers(headers).forEach((value, name) => {
		if (!['authorization', 'cookie', 'set-cookie', 'token'].includes(name.toLowerCase())) {
			safe[name] = value;
		}
	});
	return safe;
};

const hash = async (value: string) => {
	const encoded = new TextEncoder().encode(value);
	if (globalThis.crypto?.subtle) {
		const digest = await globalThis.crypto.subtle.digest('SHA-256', encoded);
		return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
	}

	let result = 2166136261;
	for (const byte of encoded) {
		result = Math.imul(result ^ byte, 16777619);
	}
	return (result >>> 0).toString(16);
};

export const getGetCacheIdentity = async (url: string, query: unknown, token?: string | null) => {
	const tokenKey = await hash(token || '');
	const key = await hash(JSON.stringify([url, query ?? null, tokenKey]));
	return { key, tokenKey };
};

const serializeCacheResponse = (response: unknown) =>
	JSON.stringify(response, (_key, value) =>
		value instanceof Map ? { __offlineCacheType: 'Map', entries: Array.from(value.entries()) } : value
	);

const deserializeCacheResponse = (response: string) =>
	JSON.parse(response, (_key, value) => {
		if (value && value.__offlineCacheType === 'Map' && Array.isArray(value.entries)) {
			return new Map(value.entries);
		}
		return value;
	});

export const getCachedGet = async (key: string) => {
	if (Capacitor.isNativePlatform()) {
		try {
			const { value } = await Preferences.get({ key: `${NATIVE_GET_CACHE_PREFIX}${key}` });
			if (value) {
				const entry = JSON.parse(value) as CachedGetRequest & { response: string };
				return { ...entry, response: deserializeCacheResponse(entry.response) };
			}
		} catch (error) {
			console.error('Failed to read native cached GET response:', error);
		}
	}

	if (typeof indexedDB === 'undefined') return null;

	try {
		const database = await openDatabase();
		const transaction = database.transaction(GET_CACHE_STORE_NAME, 'readonly');
		const completed = transactionComplete(transaction);
		const entry = await requestResult(transaction.objectStore(GET_CACHE_STORE_NAME).get(key));
		await completed;
		if (!entry) return null;
		return {
			...(entry as CachedGetRequest),
			response: deserializeCacheResponse((entry as { response: string }).response)
		};
	} catch (error) {
		console.error('Failed to read IndexedDB cached GET response:', error);
		return null;
	}
};

export const saveCachedGet = async (entry: Omit<CachedGetRequest, 'updatedAt'>) => {
	const storedEntry: CachedGetRequest & { response: string } = {
		...entry,
		response: serializeCacheResponse(entry.response),
		updatedAt: Date.now()
	};
	let saved = false;
	let lastError: unknown;

	if (Capacitor.isNativePlatform()) {
		try {
			await Preferences.set({
				key: `${NATIVE_GET_CACHE_PREFIX}${entry.key}`,
				value: JSON.stringify(storedEntry)
			});
			saved = true;
		} catch (error) {
			lastError = error;
			console.error('Failed to persist native cached GET response:', error);
		}
	}

	if (typeof indexedDB !== 'undefined') {
		try {
			const database = await openDatabase();
			const transaction = database.transaction(GET_CACHE_STORE_NAME, 'readwrite');
			const completed = transactionComplete(transaction);
			transaction.objectStore(GET_CACHE_STORE_NAME).put(storedEntry);
			await completed;
			saved = true;
		} catch (error) {
			lastError = error;
			console.error('Failed to persist IndexedDB cached GET response:', error);
		}
	}

	if (!saved && lastError) throw lastError;
};

export const getCachedGetRequests = async (tokenKey: string): Promise<CachedGetRequest[]> => {
	if (typeof indexedDB === 'undefined') return [];
	const database = await openDatabase();
	const transaction = database.transaction(GET_CACHE_STORE_NAME, 'readonly');
	const completed = transactionComplete(transaction);
	const entries = await requestResult(transaction.objectStore(GET_CACHE_STORE_NAME).getAll());
	await completed;
	return (entries as CachedGetRequest[])
		.filter((entry) => entry.tokenKey === tokenKey)
		.sort((left, right) => left.updatedAt - right.updatedAt);
};

const isQueueableMethod = (method: string): method is QueueableMethod =>
	method === 'POST' || method === 'PUT' || method === 'PATCH' || method === 'DELETE';

export const enqueueOfflineRequest = async <T>(url: string, options: UseFetchOptions<T>) => {
	const method = String(options.method || 'GET').toUpperCase();
	if (!isQueueableMethod(method)) {
		throw new Error(`Cannot queue unsupported offline request method: ${method}`);
	}
	const database = await openDatabase();
	const transaction = database.transaction(STORE_NAME, 'readwrite');
	const completed = transactionComplete(transaction);
	const entry: NewOfflineRequest = {
		url,
		method,
		query: options.query,
		body: encodeBody(options.body),
		headers: safeHeaders(options.headers as HeadersInit | undefined)
	};

	const id = await requestResult(transaction.objectStore(STORE_NAME).add({ ...entry, createdAt: Date.now() }));
	await completed;
	if (typeof window !== 'undefined') {
		window.dispatchEvent(new Event('offline-queue-updated'));
	}
	return Number(id);
};

export const getOfflineRequests = async (): Promise<OfflineRequest[]> => {
	const database = await openDatabase();
	const transaction = database.transaction(STORE_NAME, 'readonly');
	const completed = transactionComplete(transaction);
	const entries = await requestResult(transaction.objectStore(STORE_NAME).getAll());
	await completed;
	return entries as OfflineRequest[];
};

export const removeOfflineRequest = async (id: number) => {
	const database = await openDatabase();
	const transaction = database.transaction(STORE_NAME, 'readwrite');
	const completed = transactionComplete(transaction);
	transaction.objectStore(STORE_NAME).delete(id);
	await completed;
};

export const clearOfflineRequests = async () => {
	if (typeof indexedDB === 'undefined') return;
	const database = await openDatabase();
	const transaction = database.transaction([STORE_NAME, GET_CACHE_STORE_NAME], 'readwrite');
	const completed = transactionComplete(transaction);
	transaction.objectStore(STORE_NAME).clear();
	transaction.objectStore(GET_CACHE_STORE_NAME).clear();
	await completed;
};
